// Thin typed wrapper for talking to the API.
// Every call has a default AbortController timeout so a stalled request can
// never hang forever (see: "fetch with no timeout" footgun).

const BASE = '/api'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type ApiInit = RequestInit & { timeoutMs?: number }

async function request<T>(
  path: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  body?: unknown,
  init: ApiInit = {},
): Promise<T> {
  const { timeoutMs = 15000, signal: callerSignal, ...rest } = init
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)

  // if the caller passed their own signal, forward its abort
  if (callerSignal) {
    if (callerSignal.aborted) ctrl.abort()
    else callerSignal.addEventListener('abort', () => ctrl.abort(), { once: true })
  }

  try {
    const res = await fetch(`${BASE}${path}`, {
      ...rest,
      method,
      signal: ctrl.signal,
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(rest.headers ?? {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    if (!res.ok) {
      // surface the server's own error message when it sent one
      let message = `request failed: ${res.status}`
      try {
        const data = (await res.json()) as { error?: string }
        if (data && typeof data.error === 'string') message = data.error
      } catch {
        /* body was not JSON, keep the generic message */
      }
      throw new ApiError(res.status, message)
    }
    return (await res.json()) as T
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(0, `request to ${path} timed out after ${timeoutMs}ms`)
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}

export function apiGet<T>(path: string, init: ApiInit = {}): Promise<T> {
  return request<T>(path, 'GET', undefined, init)
}

export function apiPost<T>(path: string, body: unknown, init: ApiInit = {}): Promise<T> {
  return request<T>(path, 'POST', body, init)
}

export function apiPatch<T>(path: string, body: unknown, init: ApiInit = {}): Promise<T> {
  return request<T>(path, 'PATCH', body, init)
}

export function apiDelete<T>(path: string, init: ApiInit = {}): Promise<T> {
  return request<T>(path, 'DELETE', undefined, init)
}

export const fetchHealth = () =>
  apiGet<{ status: string; service: string; time: string }>('/health')
